import { useState, lazy, Suspense } from "react";
import { MessageCircle, Sparkles, Shield, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
const heroProfileWebp = "/images/hero-profile.webp";

const YOUTUBE_ID = "BZqBq2YmZEw";
import { trackClickButton } from "@/lib/tiktok-pixel";

const BookingModal = lazy(() => import("@/components/BookingModal"));

const WHATSAPP_NUMBER = "201031849849";

const HeroVideo = () => {
  const { t } = useLanguage();
  // Play-on-click pattern: poster image is the LCP candidate AND the only
  // thing rendered until a user explicitly clicks play. This eliminates
  // YouTube's "Sign in to confirm you're not a bot" gate that fires when the
  // embed auto-mounts from a fresh/headless-looking client, and also keeps
  // third-party JS off the critical path for every visitor.
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
        title={t.hero.playVideo}
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      aria-label={t.hero.playVideo}
      className="absolute inset-0 w-full h-full cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <img
        src={heroProfileWebp}
        alt={t.hero.heroImageAlt}
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="eager"
        // @ts-expect-error — fetchpriority is a valid HTML attribute that React 18 logs about; lowercase form is correct.
        fetchpriority="high"
      />
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-primary/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Play className="w-7 h-7 text-primary-foreground fill-primary-foreground ml-[2px]" />
        </div>
      </div>
    </button>
  );
};

const HeroSection = () => {
  const { t, dir } = useLanguage();
  const [bookingOpen, setBookingOpen] = useState(false);
  

  const openBooking = () => {
    trackClickButton("hero_booking", "احجز جلسة التغيير");
    setBookingOpen(true);
  };

  const openWhatsApp = () => {
    trackClickButton("hero_whatsapp", "واتساب من الهيرو");
    const msg = encodeURIComponent(t.hero.whatsAppMessage);
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <section className="min-h-[85vh] h-[85vh] md:h-auto md:min-h-[85vh] flex items-center pt-20 md:pt-0 relative overflow-hidden" style={{ contain: 'layout style paint' }} aria-label={t.hero.sectionLabel}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ contain: 'size layout style paint' }}>
          <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Content — opacity-only fade to avoid CLS from translate animations */}
            <div className={`${dir === "rtl" ? "order-2 lg:order-1" : "order-2 lg:order-2"} animate-[fadeIn_0.5s_ease-out_0.1s_both]`}>
              {/* Trust badge — reserve height to prevent shift on font swap */}
              <div className="inline-flex items-center gap-2 bg-accent/15 border border-accent/30 rounded-full px-4 py-1.5 text-sm font-medium mb-5 text-secondary-foreground min-h-[32px]">
                <Shield className="w-4 h-4 shrink-0" />
                {t.hero.trustBadge}
              </div>

              {/* H1 — reserve min-height to absorb font-swap reflow */}
              <h1 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4 min-h-[7.5rem] sm:min-h-[8.5rem] lg:min-h-[8rem] ${dir === "rtl" ? "text-right" : "text-left"}`}>
                {t.hero.headlinePre}
                <span className="text-primary">{t.hero.headlineHighlight}</span>
                {t.hero.headlinePost}
              </h1>

              {/* Description — reserve min-height for ~3 lines */}
              <p className={`text-base text-muted-foreground leading-relaxed mb-6 max-w-lg min-h-[4.5rem] ${dir === "rtl" ? "text-right" : "text-left"}`}>
                {t.hero.description}
              </p>

              <div className="flex items-center gap-2 mb-6 text-sm min-h-[20px]">
                <span className="flex h-2 w-2 relative shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="font-semibold text-destructive">{t.hero.spotsLeft}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 min-h-[44px]">
                <Button
                  size="lg"
                  onClick={openBooking}
                  data-testid="cta-hero-book"
                  className="text-base px-8 font-bold transition-all duration-200 hover:scale-[1.02] hover:shadow-lg shadow-primary/25"
                >
                  <Sparkles className={`h-5 w-5 ${dir === "rtl" ? "ml-2" : "mr-2"}`} />
                  {t.hero.ctaBook}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={openWhatsApp}
                  data-testid="cta-hero-whatsapp"
                  className="bg-[#25D366] hover:bg-[#20bd5a] text-white border-0 group text-base transition-all duration-200 hover:scale-[1.02]"
                >
                  <MessageCircle className={`h-5 w-5 ${dir === "rtl" ? "ml-2" : "mr-2"}`} />
                  {t.hero.ctaWhatsApp}
                </Button>
              </div>

              {/* Social proof — reserve fixed height (avatar row 32px + text) */}
              <div className="flex items-center gap-3 mt-6 min-h-[40px]">
                <div className={`flex shrink-0 ${dir === "rtl" ? "-space-x-2 space-x-reverse" : "-space-x-2"}`}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-primary/10 border-2 border-background flex items-center justify-center"
                    >
                      <span className="text-xs">👤</span>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-bold text-foreground">{t.hero.socialProof}</span>{t.hero.socialProofSuffix}
                </div>
              </div>
            </div>

            {/* Video — opacity-only fade to avoid CLS */}
            <div className={`${dir === "rtl" ? "order-1 lg:order-2" : "order-1 lg:order-1"} animate-[fadeIn_0.5s_ease-out_0.2s_both]`}>
              <div className="relative">
                <div className="aspect-[9/16] max-w-sm mx-auto rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 to-accent/20 shadow-2xl shadow-primary/10">
                  <HeroVideo />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
                <div className="absolute -top-4 -left-4 w-32 h-32 bg-accent/20 rounded-full blur-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {bookingOpen && (
        <Suspense fallback={null}>
          <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
        </Suspense>
      )}
    </>
  );
};

export default HeroSection;
